import React, { RefObject, useEffect, useRef, useState } from 'react';
import AnchorLink from 'react-anchor-link-smooth-scroll';

import { clsx } from 'clsx';

import { useLayoutContext } from '@ott/layout-context';
import { useMessage } from '@ott/message';

import FadeIn from 'src/components/elements/FadeIn';
import { ANCHOR_KEY } from 'src/components/pages/AboutUsPage/constants';

import { getMobileDevicePlatform } from 'src/helpers/common';

import { useInView } from 'framer-motion';

import styles from './MainScreen.scss';

const MainScreen = () => {
  const [scrollY, setScrollY] = useState(0);
  const [videoOpacity, setVideoOpacity] = useState(0.5);
  const [videoDisplay, setVideoDisplay] = useState<'block' | 'none'>('block');

  const messageApi = useMessage();
  const { isMobile } = useLayoutContext();

  const inViewRef: RefObject<HTMLParagraphElement> = useRef(null);
  const inViewport = useInView(inViewRef);
  const isIos = getMobileDevicePlatform() === 'ios';

  useEffect(() => {
    const scrollY = () => setScrollY(window.scrollY);

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', scrollY);
    }

    return () => {
      window.removeEventListener('scroll', scrollY);
    };
  }, []);

  useEffect(() => {
    if (scrollY > 1080) {
      setVideoOpacity(Math.max(0, (1440 - scrollY) / 720));
    }
  }, [scrollY]);

  useEffect(() => {
    if (videoOpacity > 0) {
      setVideoDisplay('block');
    } else {
      setVideoDisplay('none');
    }
  }, [videoOpacity]);

  useEffect(() => {
    if (__SERVER__ || isMobile) {
      return;
    }

    if (inViewport) {
      messageApi.open(
        <AnchorLink href="#secondScreen" className={styles.scrollDownText}>
          Scroll down
        </AnchorLink>,
        {
          placement: 'bottom',
          duration: Infinity,
          key: ANCHOR_KEY,
          className: styles.scrollDownButton,
          childrenClassNames: {
            container: styles.messageContainer,
          },
        }
      );
    } else {
      messageApi.close(ANCHOR_KEY);
    }
  }, [inViewport, messageApi, isMobile]);

  return (
    <div className={styles.mainScreenContainer}>
      {isIos ? (
        <img
          src="https://static.solartrip.com/images/about_us_landing/about_us_header.png"
          className={styles.mainScreenBackground}
          style={{ opacity: videoOpacity, display: videoDisplay }}
        />
      ) : (
        <video
          className={styles.mainScreenBackground}
          autoPlay={true}
          preload="auto"
          loop={true}
          muted={true}
          playsInline={true}
          style={{ opacity: videoOpacity, display: videoDisplay }}
        >
          <source src="https://static.solartrip.com/mp4/about_us/intro.mp4" type="video/mp4" />
        </video>
      )}

      <FadeIn>
        <div className={styles.firstScreen}>
          <p ref={inViewRef} className={clsx(styles.title, styles.titleThin)}>
            We fuse {isMobile && <br />}
            <span className={styles.title}>travel and tech</span>
          </p>
          <p className={clsx(styles.title, styles.titleThin)}>to push the boundaries</p>
          <p className={clsx(styles.title, styles.titleThin)}>of self-discovery</p>
        </div>
      </FadeIn>
      <FadeIn className={styles.secondScreen} id="secondScreen">
        <div className={clsx(styles.title, styles.titleThin)}>Embark with</div>
        <div className={styles.title}>Solartrip</div>
        <div className={styles.text}>
          We&rsquo;re living on&nbsp;a&nbsp;planet full of&nbsp;endless possibilities, hundreds
          of&nbsp;countries to&nbsp;discover, thousands of&nbsp;experiences to&nbsp;live, and
          it&nbsp;makes our life extremely interesting, but also overwhelming and challenging.
          We&nbsp;craft Solartrip with a&nbsp;focus on&nbsp;a&nbsp;world where each choice shapes
          our journey, where the power of&nbsp;a&nbsp;reliable service with personalized approach,
          honest opinions and expert support, holds immense value.
        </div>
        <div className={styles.text}>
          As&nbsp;an&nbsp;innovative and forward-thinking travel solution, we&nbsp;embody everything
          that affluent travelers need, but go&nbsp;even further and beyond their expectations.
          By&nbsp;embracing new concepts and smart technologies, we&nbsp;stay ahead of&nbsp;the
          curve in&nbsp;the ever-evolving travel landscape.
        </div>
      </FadeIn>
    </div>
  );
};

export default MainScreen;
